import { useState } from 'react'
import { Space, Typography, Tooltip, Row, Col, Form, Modal as ModalAntd, notification } from 'antd'
import { SettingOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { useAsync } from 'react-use'
import _ from 'lodash'
import { useHistory } from 'react-router-dom'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Table from 'components/Table'
import Modal from 'components/Modal'
import Input from 'components/Input'
import TextArea from 'components/TextArea'
import Select from 'components/Select'
import AllActiveInactive from 'components/AllActiveInactive'

import { useTranslate } from 'hooks/translate'

import {
  getDictionaryListApi,
  createDictionaryApi,
  deleteDictionaryByIdApi,
  updateDictionaryByIdApi,
} from 'services/dictionary'

import Styled from './styled'

const DEFAULT_PAGE_SIZE = 10

export default function PageDictionary () {
  const { t } = useTranslate()
  const history = useHistory()

  const [form] = Form.useForm()

  const [dataSource, setDataSource] = useState([])
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useState({
    param: null,
    page: 1,
    size: DEFAULT_PAGE_SIZE,
  })
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all') // all, active, inactive
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const _list = async () => {
    if (loading) return

    const params = {
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setLoading(true)

    const res = await getDictionaryListApi(params)

    setLoading(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    setTotal(res.data?.total)
    setDataSource(res.data?.items)
  }

  const _create = async (values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await createDictionaryApi({
      dictionary_name: values?.name,
      dictionary_description: values?.description,
      is_active: values?.isEnabled,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form.resetFields()
  }

  const _update = async (id, values) => {
    if (submitting) return

    setSubmitting(true)

    const res = await updateDictionaryByIdApi({
      id,
      dictionary_name: values?.name,
      dictionary_description: values?.description,
      is_active: values?.isEnabled,
    })

    setSubmitting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()

    setShowCreateModal(false)

    form.resetFields()
  }

  const _delete = async (id) => {
    if (deleting) return

    setDeleting(true)

    const res = await deleteDictionaryByIdApi(id)

    setDeleting(false)

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })

      return
    }

    _list()
  }

  useAsync(async () => {
    _list()
  }, [status, filters])

  const columns = [
    {
      title: t('Name'),
      dataIndex: 'name',
      key: 'name',
      className: 'bold',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      sorter: (a, b) => a.description.localeCompare(b.description),
    },
    {
      title: t('Status'),
      dataIndex: ['status', 'label'],
      key: 'status',
      className: 'is-status',
      sorter: (a, b) => a.status.label.localeCompare(b.status.label),
      render: (text, record, index) => {
        return (
          <div
            className={classNames('status-block', {
              'is-active': record.isEnabled,
              'is-inactive': !record.isEnabled,
            })}
          >
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      render: (text, record, index) => {
        return (
          <Space size='small'>
            <Tooltip title={t('View')}>
              <Button
                type='link'
                icon={<EyeOutlined />}
                onClick={() => {
                  history.push(`${history.location.pathname}/${record.id}`)
                }}
              />
            </Tooltip>
            <Tooltip title={t('Edit')}>
              <Button
                type='link'
                icon={<SettingOutlined />}
                onClick={() => {
                  setEditingId(record.id)

                  const editingDataSource = _.find(dataSource, { id: record.id })

                  form.setFieldsValue({
                    name: editingDataSource.name,
                    description: editingDataSource.description,
                    isEnabled: editingDataSource.isEnabled,
                  })

                  setShowCreateModal(true)
                }}
              />
            </Tooltip>
            <Tooltip title={t('Delete')}>
              <Button
                type='link'
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  const deletingDataSource = _.find(dataSource, { id: record.id })

                  ModalAntd.confirm({
                    title: 'Confirm deletion: Dictionary',
                    icon: null,
                    maskClosable: true,
                    closeIcon: true,
                    content: (
                      <div>
                        <dl>
                          <dt>Dictionary Name</dt>
                          <dd>- {deletingDataSource.name}</dd>
                          <dt>Dictionary Description</dt>
                          <dd>- {deletingDataSource.description}</dd>
                        </dl>
                      </div>
                    ),
                    okButtonProps: {
                      shape: 'round',
                    },
                    okText: 'Delete',
                    okType: 'primary',
                    cancelButtonProps: {
                      shape: 'round',
                    },
                    cancelText: 'Cancel',
                    onOk: async () => {
                      await _delete(record?.id)
                    },
                  })
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  return (
    <Styled className='page page-dictionary'>
      <Page
        title={t('Dictionary')}
        searchPlaceholder={t('Search Dictionary or description')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
            page: 1,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Row justify='space-between'>
            <Col></Col>
            <Col>
              <Button type='primary' size='large' onClick={() => setShowCreateModal(true)}>
                {t('Create New Dictionary')}
              </Button>
            </Col>
          </Row>
          <Card title={t('Dictionary')}>
            <Col xs={24} sm={8} lg={4}>
              <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
            </Col>
            <Table
              loading={loading}
              columns={columns}
              dataSource={dataSource}
              pagination={{
                current: filters.page,
                pageSize: filters.size,
                total,
                showQuickJumper: true,
                showSizeChanger: true,
                showTotal: (total) => t('Total ${total} items', { total }),
              }}
              onChange={(pagination, filters) => {
                setFilters({
                  ...filters,
                  page: pagination.current,
                  size: pagination.pageSize,
                })
              }}
              scroll={{ x: 800 }}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        width={600}
        visible={showCreateModal}
        title={editingId ? t('Update Dictionary') : t('Create New Dictionary')}
        onCancel={() => {
          setShowCreateModal(false)
        }}
        afterClose={() => {
          if (editingId) {
            form.resetFields()
          }

          setEditingId(null)
        }}
        footer={[
          <Button
            key='create'
            type='primary'
            onClick={() => {
              form.submit()
            }}
          >
            {editingId ? t('Update') : t('Create')}
          </Button>,
        ]}
      >
        <div>
          <Form
            form={form}
            layout='vertical'
            initialValues={{
              isEnabled: true,
            }}
            onFinish={(values) => {
              if (editingId) {
                _update(editingId, values)
              } else {
                _create(values)
              }
            }}
          >
            <Form.Item label={t('Dictionary Name')} name='name' rules={[{ required: true }]}>
              <Input />
            </Form.Item>

            <Form.Item label={t('Dictionary Description')} name='description'>
              <TextArea />
            </Form.Item>

            <Row justify='space-between'>
              <Col>
                <Form.Item name='isEnabled' label='Status'>
                  <Select
                    options={[
                      { label: t('Active'), value: true },
                      { label: t('Inactive'), value: false },
                    ]}
                    style={{ width: 200, textAlign: 'center' }}
                  />
                </Form.Item>
              </Col>
              <Col></Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </Styled>
  )
}
